import React, { useState, useCallback } from 'react';
import { Form, Button, Container, Row, Col, Card, Tooltip, OverlayTrigger, ListGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import UnidadeViewModel from '../../viewmodel/UnidadeViewModel';
import useUnidadeStore from '../../stores/unidadeStore';
import { debounce } from 'lodash';
import Usuario, { UsuarioUtils } from '../../model/Usuario/Usuario';
import { toast } from 'react-toastify';

interface UnidadeCadastroProps {
    user: Usuario;
}

const UnidadeCadastro: React.FC<UnidadeCadastroProps> = ({ user }) => {
    const { refreshUnidades } = useUnidadeStore();
    const [nomeExistente, setNomeExistente] = useState<boolean>(false);
    const [unidadesSemelhantes, setUnidadesSemelhantes] = useState<string[]>([]);

    const verificarNomeDebounced = useCallback(
        debounce(async (nome: string) => {
            try {
                if (nome.length > 0) {
                    const unidades = await UnidadeViewModel.buscarUnidadesPorNome(nome, 0, 6, 'nome,ASC');
                    const nomeExatoExistente = unidades.some((u) => u.nome.toLowerCase() === nome.toLowerCase());
                    setNomeExistente(nomeExatoExistente);
                    setUnidadesSemelhantes(unidades.map((u) => u.nome));
                } else {
                    setNomeExistente(false);
                    setUnidadesSemelhantes([]);
                }
            } catch (error) {
                console.error('Erro ao verificar se o nome da unidade já existe:', error);
            }
        }, 500), []);

    const validationSchema = Yup.object().shape({
        nome: Yup.string()
            .required('O nome da unidade é obrigatório')
            .test('checkUniqueNome', 'Já existe uma unidade com esse nome.', async (value) => {
                if (value.length) {
                    verificarNomeDebounced(value);
                }
                return !nomeExistente;
            }),
        clientId: Yup.string()
    });

    const handleSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
        setSubmitting(true);
        try {
            const unidade = await UnidadeViewModel.criarUnidade(values);

            if (unidade) {
                toast.success('Unidade criada com sucesso!');
                await refreshUnidades(); // Atualiza o cache da store
                setNomeExistente(false);
                setUnidadesSemelhantes([]);
                resetForm();
            } else {
                toast.error('Erro ao criar unidade: unidade não retornada.');
            }
        } catch (error) {
            console.error('Erro ao criar unidade:', error);
            toast.error('Erro ao criar unidade');
        } finally {
            setSubmitting(false);
        }
    };

    const renderTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            Deve ser preenchido pelo profissional da TI. Usado para a autenticação do APP da Unidade na Sensedia (pode ser encontrado na Sensedia).
        </Tooltip>
    );

    return (
        <Container className="mt-5">
            <Row className="justify-content-md-center">
                <Col md={8}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Cadastro de Unidade/Órgão</Card.Title>
                            <Formik
                                initialValues={{ nome: '', clientId: '' }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                    isSubmitting,
                                }) => (
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Form.Group controlId="nome" style={{ marginTop: '35px' }}>
                                            <Form.Label>Nome</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nome da Unidade"
                                                value={values.nome}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    verificarNomeDebounced(e.target.value); // Verificar nome com debounce
                                                }}
                                                onBlur={handleBlur}
                                                isInvalid={touched.nome && !!errors.nome}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nome}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {/* Lista de unidades semelhantes */}
                                        {unidadesSemelhantes.length > 0 && (
                                            <ListGroup className="mb-3">
                                                <ListGroup.Item variant="warning">
                                                    Unidades/Órgãos com nome semelhante:
                                                </ListGroup.Item>
                                                {unidadesSemelhantes.map((unidade, index) => (
                                                    <ListGroup.Item key={index}>
                                                        {unidade}
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                        {UsuarioUtils.verificarTodasPermissoes(user, []) ?
                                            <>
                                                <Form.Group controlId="clientId" className="mt-3">
                                                    <Form.Label style={{ marginTop: '15px' }}>client_id - Sensedia <div style={{ color: 'gray' }}></div></Form.Label>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        delay={{ show: 250, hide: 400 }}
                                                        overlay={renderTooltip}
                                                    >
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="ID do Cliente"
                                                            value={values.clientId}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={touched.clientId && !!errors.clientId}
                                                        />
                                                    </OverlayTrigger>
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.clientId}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </>
                                            : <></>}
                                        <div style={{ marginTop: '25px' }}></div>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="mt-3"
                                            disabled={isSubmitting}

                                        >
                                            Cadastrar
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default UnidadeCadastro;
